import React from "react";
import { NavLink, useHistory, useParams } from "react-router-dom";
import { match } from "ts-pattern";

import { CTA } from "src/components/cta/cta";
import { Separator } from "src/components/separator/separator";
import { School } from "src/generated/types";
import { Routes } from "src/utils/routes";
import { ReportTabs } from "src/components/report_tabs/report_tabs";
import { useDocumentTitle } from "src/utils/use_document_title";

import { Practical } from "./sections/practical/practical";
import { Intellectual } from "./sections/intellectual/intellectual";
import { Nurture } from "./sections/nurture/nurture";
import { Heart } from "./sections/heart/heart";
import { Head } from "./sections/head/head";
import { Hands } from "./sections/hands/hands";
import { MeaningfulStrengths } from "./sections/meaningful_strengths/meaningful_strengths";
import { KeyGrowthOpportunities } from "./sections/key_growth_opportunities/key_growth_opportunities";
import { Overview } from "./sections/overview/overview";
import { FFIBrandedV1Report } from "./types";
import "../../report.scss";

/*
 * Types
 */

interface ReportDetailPageProps {
    readonly report: FFIBrandedV1Report;
    readonly school: School;
}

type SectionPath =
    | "intellectual"
    | "nurture"
    | "overview"
    | "head"
    | "hands"
    | "heart"
    | "practical"
    | "strength-and-growth-opportunities";

interface SectionUrlPath {
    readonly section: SectionPath;
}

/*
 * Main
 */

export const ReportPartialPage = (props: ReportDetailPageProps): React.ReactElement => {
    const { report, school } = props;

    useDocumentTitle("Flourishing Faith Index");

    const history = useHistory();
    const { section } = useParams<SectionUrlPath>();

    const scrollAndPush = (toPush: string): void => {
        history.push(toPush);
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const getLink = (section: string) => {
        return `/reports/${report.value.type}/${report.value.year}/${section}`;
    };

    const renderDomainLinks = (): React.ReactElement => {
        return (
            <>
                <NavLink className="step-link" exact to={getLink(Routes.section.intellectual)}>
                    Intellectual
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.nurture)}>
                    Nurture
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.practical)}>
                    Practical
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.head)}>
                    Head
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.hands)}>
                    Hands
                </NavLink>
                <NavLink className="step-link" exact to={getLink(Routes.section.heart)}>
                    Heart
                </NavLink>
            </>
        );
    };

    const renderDomain = (): React.ReactElement => {
        return match(section)
            .with("intellectual", () => {
                return (
                    <>
                        <Intellectual report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.nurture))}
                            subtitle="Next:"
                            title="Explore the Nurture Domain"
                        />
                    </>
                );
            })
            .with("nurture", () => {
                return (
                    <>
                        <Nurture report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.practical))}
                            subtitle="Next:"
                            title="Explore the Practical Domain"
                        />
                    </>
                );
            })
            .with("overview", () => {
                return (
                    <>
                        <Overview />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.strengthAndGrowthOpportunities))}
                            subtitle="Next:"
                            title="Explore Strengths and Growth Opportunities"
                        />
                    </>
                );
            })
            .with("practical", () => {
                return (
                    <>
                        <Practical report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.head))}
                            subtitle="Next:"
                            title="Explore the Head Domain"
                        />
                    </>
                );
            })
            .with("head", () => {
                return (
                    <>
                        <Head report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.hands))}
                            subtitle="Next:"
                            title="Explore the Hands Domain"
                        />
                    </>
                );
            })
            .with("hands", () => {
                return (
                    <>
                        <Hands report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.heart))}
                            subtitle="Next:"
                            title="Explore the Heart Domain"
                        />
                    </>
                );
            })
            .with("heart", () => {
                return (
                    <>
                        <Heart report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.intellectual))}
                            subtitle="Next:"
                            title="Explore the Intellectual Domain"
                        />
                    </>
                );
            })
            .with("strength-and-growth-opportunities", () => {
                return (
                    <>
                        <MeaningfulStrengths report={report} />
                        <KeyGrowthOpportunities report={report} />
                        <CTA
                            color="blue"
                            icon="chart"
                            onClick={(): void => scrollAndPush(getLink(Routes.section.intellectual))}
                            subtitle="Next:"
                            title="Explore the Six Domains"
                        />
                    </>
                );
            })
            .exhaustive();
    };

    return (
        <>
            <div className="report-page-top">
                <ReportTabs currentReport={report} school={school} />
                <Separator />
                <div className="report-internal-navigation">
                    <div className="internal-nav-top-line">
                        <NavLink className="step-link" exact to={getLink(Routes.section.overview)}>
                            Overview
                        </NavLink>
                        <NavLink
                            className="step-link"
                            exact
                            to={getLink(Routes.section.strengthAndGrowthOpportunities)}
                        >
                            Strengths and Growth Opportunities
                        </NavLink>
                        <div className="clear" />
                    </div>
                    <div className="internal-nav-bottom-line">{renderDomainLinks()}</div>
                </div>
            </div>
            {renderDomain()}
            <div className="internal-nav-bottom-line center">{renderDomainLinks()}</div>
        </>
    );
};
